/**
 * Service for handling A/B test data
 */

interface VariantData {
  id: string;
  name: string;
  visitors: number;
  conversions: number;
  conversionRate: number;
  engagementRate: number;
  avgTimeOnPage: string;
  bounceRate: number;
}

interface ABTestData {
  totalVisitors: number;
  variants: VariantData[];
}

// Mock data - In a real implementation, this would come from an API
const mockData: ABTestData = {
  totalVisitors: 2458,
  variants: [
    {
      id: 'exhausted',
      name: 'Exhausted Marketer',
      visitors: 612,
      conversions: 73,
      conversionRate: 11.9,
      engagementRate: 42.3,
      avgTimeOnPage: '2:47',
      bounceRate: 31.2,
    },
    {
      id: 'sdr-cost',
      name: 'SDR ROI',
      visitors: 601,
      conversions: 54,
      conversionRate: 9.0,
      engagementRate: 38.7,
      avgTimeOnPage: '2:21',
      bounceRate: 35.8,
    },
    {
      id: 'soul',
      name: 'Soul Leaving',
      visitors: 623,
      conversions: 81,
      conversionRate: 13.0,
      engagementRate: 45.1,
      avgTimeOnPage: '3:12',
      bounceRate: 28.4,
    },
    {
      id: 'clicker',
      name: 'Clicker',
      visitors: 622,
      conversions: 65,
      conversionRate: 10.5,
      engagementRate: 40.2,
      avgTimeOnPage: '2:35',
      bounceRate: 33.1,
    },
  ],
};

/**
 * Fetch A/B test data
 * @param dateRange The date range to fetch data for (e.g., '7d', '30d')
 * @returns Promise that resolves to the A/B test data
 */
export const fetchABTestData = async (dateRange: string = '7d'): Promise<ABTestData> => {
  // In a real implementation, this would make an API call
  return new Promise((resolve) => {
    // Simulate API delay
    setTimeout(() => {
      // In a real implementation, we would fetch different data based on the date range
      resolve(mockData);
    }, 500);
  });
};

/**
 * Set the default landing page variant
 * @param variantId The ID of the variant to set as default
 * @returns Promise that resolves to a success message
 */
export const setDefaultVariant = async (variantId: string): Promise<{ success: boolean; message: string }> => {
  // In a real implementation, this would make an API call
  return new Promise((resolve) => {
    // Simulate API delay
    setTimeout(() => {
      // Validate the variant ID
      const validVariants = mockData.variants.map(v => v.id);
      if (!validVariants.includes(variantId)) {
        resolve({ 
          success: false, 
          message: `Invalid variant ID: ${variantId}` 
        });
        return;
      }
      
      // In a real implementation, we would update the default variant in the database
      resolve({ 
        success: true, 
        message: `Successfully set ${variantId} as the default landing page variant` 
      });
    }, 500);
  });
};

/**
 * Track an A/B test event
 * @param eventType The type of event (e.g., 'conversion', 'engagement')
 * @param eventName The name of the event (e.g., 'start_trial', 'page_view')
 * @param variantId The ID of the variant
 * @param additionalData Additional data to track with the event
 * @returns Promise that resolves to a success message
 */
export const trackABTestEvent = async (
  eventType: 'conversion' | 'engagement',
  eventName: string,
  variantId: string,
  additionalData: Record<string, any> = {}
): Promise<{ success: boolean; message: string }> => {
  // In a real implementation, this would make an API call
  return new Promise((resolve) => {
    // Simulate API delay
    setTimeout(() => {
      // In a real implementation, we would send the event to the analytics service
      console.log(`Tracked ${eventType} event "${eventName}" for variant "${variantId}"`, additionalData);
      
      resolve({ 
        success: true, 
        message: `Successfully tracked ${eventType} event "${eventName}" for variant "${variantId}"` 
      });
    }, 100);
  });
};

/**
 * Create a new variant
 * @param variantData The data for the new variant
 * @returns Promise that resolves to the created variant
 */
export const createVariant = async (
  variantData: Omit<VariantData, 'visitors' | 'conversions' | 'conversionRate' | 'engagementRate' | 'avgTimeOnPage' | 'bounceRate'>
): Promise<{ success: boolean; message: string; variant?: VariantData }> => {
  // In a real implementation, this would make an API call
  return new Promise((resolve) => {
    // Simulate API delay
    setTimeout(() => {
      // Validate the variant ID
      const existingVariantIds = mockData.variants.map(v => v.id);
      if (existingVariantIds.includes(variantData.id)) {
        resolve({ 
          success: false, 
          message: `Variant ID ${variantData.id} already exists` 
        });
        return;
      }
      
      // In a real implementation, we would create the variant in the database
      const newVariant: VariantData = {
        ...variantData,
        visitors: 0,
        conversions: 0,
        conversionRate: 0,
        engagementRate: 0,
        avgTimeOnPage: '0:00',
        bounceRate: 0,
      };
      
      resolve({ 
        success: true, 
        message: `Successfully created variant ${variantData.name}`,
        variant: newVariant
      });
    }, 500);
  });
}; 